import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

// Parse float age into years, months, days (truncating instead of rounding)
function parseAge(age, returnType = "string") {
  const ageStr = age.toFixed(3).toString();
  const [yearsStr, decimalStr = "0"] = ageStr.split(".");
  const years = parseInt(yearsStr, 10);

  const monthsFloat = parseFloat(`0.${decimalStr}`) * 12;
  const months = Math.floor(monthsFloat);
  const days = Math.floor((monthsFloat - months) * 30); // Use floor instead of round

  const ageArray = { years, months, days };

  if (returnType === "string") {
    return ageToString(ageArray, false); // exclude days
  }

  return ageArray;
}

// Convert age array to human-readable string
function ageToString(age, includeDays = false) {
  const parts = [];
  if (age.years) parts.push(`${age.years}y`);
  if (age.months) parts.push(`${age.months}m`);
  if (includeDays && age.days) parts.push(`${age.days}d`);
  return parts.length ? parts.join(", ") : "At Birth";
}

// Calculate DOB from radiograph date and dental age
function calculateReportedDobFromAge(project, dentalAge, isArray = false) {
  let dob = "Unknown";

  if (dentalAge && project.radiograph_date) {
    const age = isArray ? dentalAge : parseAge(dentalAge, "array");

    if (
      typeof age.years === "number" &&
      typeof age.months === "number" &&
      typeof age.days === "number"
    ) {
      const radiographDate = dayjs.utc(project.radiograph_date);
      dob = radiographDate
        .subtract(age.years, "year")
        .subtract(age.months, "month")
        .subtract(age.days, "day")
        .format("DD MMM YYYY");
    }
  }

  return dob;
}

// Main logic for age and DOB processing
export const processFinalResult = (final_result, project) => {
  let standardDeviation = final_result.standard_deviation;
  let new_calendar_age = final_result.age;

  if (final_result.selected_age === "upper_limit") {
    new_calendar_age = final_result.upper_limit;
  } else if (final_result.selected_age === "lower_limit") {
    new_calendar_age = final_result.lower_limit;
  }

  // Apply base multiplier
  new_calendar_age *= 1.0146;
  const new_sd = standardDeviation * 1.0146;

  // Ethnicity-based adjustments
  let ethnicity = "";
  if (project?.ethnicity) {
    ethnicity = project.ethnicity.toLowerCase();
  }

  if (ethnicity) {
    const isAfrican = ethnicity.includes("african");
    const isAsian = ethnicity.includes("asian");

    if (final_result.exam_id === 2) {
      if (isAfrican) new_calendar_age += 0.33;
      else if (isAsian) new_calendar_age = Math.max(new_calendar_age - 0.33, 0);
    } else if (final_result.exam_id === 3) {
      if (isAfrican) new_calendar_age += 0.66;
      else if (isAsian) new_calendar_age = Math.max(new_calendar_age - 0.66, 0);
    } else if (final_result.exam_id === 4) {
      if (isAfrican) new_calendar_age += 0.99;
      else if (isAsian) new_calendar_age = Math.max(new_calendar_age - 0.99, 0);
    }
  }

  const ageArray = parseAge(new_calendar_age, "array");
  const biologicalAge =
    new_calendar_age > 0 ? ageToString(ageArray, false) : "At Birth";

  const lowerRange = parseAge(new_calendar_age - new_sd, "array");
  const upperRange = parseAge(new_calendar_age + new_sd, "array");
  const biologicalAgeRange = `${ageToString(
    lowerRange,
    false
  )} to ${ageToString(upperRange, false)}`;

  const biologicalDob = calculateReportedDobFromAge(project, ageArray, true);

  return {
    biologicalAge,
    ageArray,
    biologicalAgeRange,
    biologicalDob,
  };
};
